import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Bell,
  User,
  CheckCircle,
  AlertCircle,
  Mail,
  Calendar,
  Ticket,
  Settings,
  Trash2,
  Check,
  Loader2,
} from "lucide-react";
import PageWrapper from "../../components/layout/PageWrapper";
import Button from "../../components/forms/Button";
import { notificationService } from "../../api/notificationService";

function formatRelativeTime(dateStr) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60)
    return `${diffMins} minute${diffMins !== 1 ? "s" : ""} ago`;
  if (diffHours < 24)
    return `${diffHours} hour${diffHours !== 1 ? "s" : ""} ago`;
  if (diffDays < 7) return `${diffDays} day${diffDays !== 1 ? "s" : ""} ago`;
  return date.toLocaleDateString();
}

const NotificationCenter = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });
  const [loadingMore, setLoadingMore] = useState(false);
  const [actioningId, setActioningId] = useState(null);

  const fetchNotifications = useCallback(
    async (page = 1, append = false) => {
      const isAppend = append && page > 1;
      if (isAppend) setLoadingMore(true);
      else setLoading(true);
      setError(null);
      try {
        const unreadOnly = filter === "unread";
        const res = await notificationService.getNotifications({
          page,
          limit: 30,
          unreadOnly: unreadOnly || undefined,
        });
        const list = res.data || [];
        const pag = res.pagination || {};
        setPagination({
          page: pag.page || 1,
          totalPages: pag.totalPages || 1,
          total: pag.total || 0,
        });
        // Newest first: API returns sorted by createdAt desc; keep order after append/merge
        setNotifications((prev) => {
          const merged = isAppend ? [...prev, ...list] : list;
          return [...merged].sort(
            (a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0),
          );
        });
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load notifications");
        if (!isAppend) setNotifications([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [filter],
  );

  useEffect(() => {
    fetchNotifications(1);
  }, [fetchNotifications]);

  const getNotificationIcon = (type) => {
    const t = (type || "").toLowerCase();
    if (
      t.includes("task") ||
      t === "task_assigned" ||
      t === "task_wait_to_review"
    ) {
      return { icon: Bell, iconBg: "from-primary-accent to-orange-600" };
    }
    if (t.includes("admin") || t === "team") {
      return { icon: User, iconBg: "from-blue-500 to-blue-600" };
    }
    if (t === "success")
      return { icon: CheckCircle, iconBg: "from-green-500 to-green-600" };
    if (t === "meeting")
      return { icon: Calendar, iconBg: "from-purple-500 to-purple-600" };
    if (t === "alert")
      return { icon: AlertCircle, iconBg: "from-red-500 to-red-600" };
    if (t === "message" || t.includes("channel"))
      return { icon: Mail, iconBg: "from-indigo-500 to-indigo-600" };
    if (t === "ticket")
      return { icon: Ticket, iconBg: "from-yellow-500 to-yellow-600" };
    if (t === "request")
      return { icon: User, iconBg: "from-cyan-500 to-cyan-600" };
    if (t === "system")
      return { icon: Settings, iconBg: "from-gray-500 to-gray-600" };
    return { icon: Bell, iconBg: "from-primary-accent to-orange-600" };
  };

  const handleMarkAsRead = async (id) => {
    setActioningId(id);
    try {
      await notificationService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n._id === id ? { ...n, read: true } : n)),
      );
    } catch (err) {
      console.error("Mark as read failed:", err);
    } finally {
      setActioningId(null);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch (err) {
      console.error("Mark all as read failed:", err);
    }
  };

  const handleClearAll = async () => {
    try {
      await notificationService.deleteAllNotifications();
      setNotifications([]);
      setPagination({ page: 1, totalPages: 1, total: 0 });
    } catch (err) {
      console.error("Clear all failed:", err);
    }
  };

  const handleDelete = async (id) => {
    setActioningId(id);
    try {
      await notificationService.deleteNotification(id);
      setNotifications((prev) => prev.filter((n) => n._id !== id));
    } catch (err) {
      console.error("Delete failed:", err);
    } finally {
      setActioningId(null);
    }
  };

  const handleNotificationClick = (notification) => {
    const url = notification.data?.url;
    if (url) navigate(url);
    if (!notification.read) handleMarkAsRead(notification._id);
  };

  const loadMore = () => {
    const next = pagination.page + 1;
    if (next <= pagination.totalPages) fetchNotifications(next, true);
  };

  const unreadCount = notifications.filter((n) => !n.read).length;
  const filteredList =
    filter === "unread"
      ? notifications.filter((n) => !n.read)
      : filter === "read"
        ? notifications.filter((n) => n.read)
        : notifications;

  return (
    <PageWrapper
      title="Notification Center"
      subtitle={
        loading && !notifications.length
          ? "Loading..."
          : `Newest first. You have ${unreadCount} unread. Notifications remain until you mark as read or clear.`
      }
      actions={
        <div className="flex gap-3">
          <Button variant="ghost" onClick={handleClearAll}>
            Clear All
          </Button>
          <Button onClick={handleMarkAllAsRead} disabled={unreadCount === 0}>
            Mark All as Read
          </Button>
        </div>
      }
    >
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setFilter("all")}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === "all"
              ? "bg-primary-accent text-white"
              : "bg-gray-100 dark:bg-dark-300 text-gray-700 dark:text-light-300 hover:bg-gray-200 dark:hover:bg-dark-200"
          }`}
        >
          All ({pagination.total || notifications.length})
        </button>
        <button
          onClick={() => setFilter("unread")}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === "unread"
              ? "bg-primary-accent text-white"
              : "bg-gray-100 dark:bg-dark-300 text-gray-700 dark:text-light-300 hover:bg-gray-200 dark:hover:bg-dark-200"
          }`}
        >
          Unread ({unreadCount})
        </button>
        <button
          onClick={() => setFilter("read")}
          className={`px-4 py-2 rounded-lg font-medium transition-colors ${
            filter === "read"
              ? "bg-primary-accent text-white"
              : "bg-gray-100 dark:bg-dark-300 text-gray-700 dark:text-light-300 hover:bg-gray-200 dark:hover:bg-dark-200"
          }`}
        >
          Read ({(pagination.total || notifications.length) - unreadCount})
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-100 dark:bg-red-900/20 border border-red-400 rounded-lg">
          <p className="text-red-700 dark:text-red-400">{error}</p>
        </div>
      )}

      <div className="space-y-3">
        {loading && !notifications.length ? (
          <div className="card flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-accent" />
          </div>
        ) : filteredList.length > 0 ? (
          <>
            {filteredList.map((notification, index) => {
              const { icon: Icon, iconBg } = getNotificationIcon(
                notification.data?.type,
              );
              const unread = !notification.read;
              const isActioning = actioningId === notification._id;
              return (
                <motion.div
                  key={notification._id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  className={`card hover:shadow-lg transition-all duration-200 cursor-pointer ${
                    unread ? "border-l-4 border-l-primary-accent" : ""
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex gap-4">
                    <div
                      className={`w-12 h-12 rounded-full bg-gradient-to-br ${iconBg} flex items-center justify-center text-white flex-shrink-0`}
                    >
                      <Icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-light-100">
                          {notification.title}
                        </h3>
                        {unread && (
                          <span className="w-2.5 h-2.5 bg-primary-accent rounded-full flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-light-400 mb-2">
                        {notification.body || notification.title}
                      </p>
                      <div className="flex items-center justify-between">
                        <p className="text-xs text-gray-500 dark:text-light-500">
                          {formatRelativeTime(notification.createdAt)}
                        </p>
                        <div
                          className="flex gap-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {unread && (
                            <button
                              onClick={() => handleMarkAsRead(notification._id)}
                              disabled={isActioning}
                              className="p-1.5 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/20 text-green-600 dark:text-green-400 transition-colors"
                              title="Mark as read"
                            >
                              {isActioning ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(notification._id)}
                            disabled={isActioning}
                            className="p-1.5 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
            {pagination.page < pagination.totalPages && (
              <div className="flex justify-center pt-4">
                <Button
                  variant="outline"
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Load more"
                  )}
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="card text-center py-12">
            <Bell className="w-16 h-16 text-gray-300 dark:text-dark-100 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-light-100 mb-2">
              No notifications
            </h3>
            <p className="text-sm text-gray-500 dark:text-light-500">
              {filter === "unread"
                ? "You're all caught up!"
                : filter === "read"
                  ? "No read notifications"
                  : "You don't have any notifications yet. They will appear here when you receive task assignments, reviews, and more."}
            </p>
          </div>
        )}
      </div>
    </PageWrapper>
  );
};

export default NotificationCenter;
